import DocsLayout, { DocSection, Steps, Callout } from "@/components/DocsLayout";

const SECTIONS = [
  { id: "overview", title: "1. Admin overview" },
  { id: "workspace", title: "2. Workspace & branding" },
  { id: "users", title: "3. Users, roles & invites" },
  { id: "billing", title: "4. Billing & plan limits" },
  { id: "categories", title: "5. Custom categories" },
  { id: "teller", title: "6. Bank Connect setup (Teller MTLS)" },
  { id: "platform-admin", title: "7. Platform admin (cross-tenant)" },
  { id: "compliance", title: "8. Compliance (close periods, audit)" },
  { id: "demo-tenants", title: "9. Demo tenants & background crons" },
  { id: "data", title: "10. Data lifecycle & deletes" },
  { id: "security", title: "11. Security & compliance" },
  { id: "troubleshooting", title: "12. Troubleshooting" },
];

export default function AdminManual() {
  return (
    <DocsLayout
      eyebrow="Documentation"
      title="Admin manual"
      lastUpdated="February 2026"
      sections={SECTIONS}
    >
      <DocSection id="overview" title="1. Admin overview">
        <p>
          This guide is for workspace owners and admins. It covers the controls that affect every
          user in the tenant — billing, branding, invites, and bank-connect setup.
        </p>
        <ul className="list-disc pl-5 space-y-1.5 text-sm">
          <li><strong>Owner</strong> — root account, manages billing, can demote / delete admins.</li>
          <li><strong>Admin</strong> — manages users, branding, categories, and integrations.</li>
          <li><strong>Accountant</strong> — full read/write on financial data, no admin controls.</li>
          <li><strong>Viewer</strong> — read-only access to dashboards and reports.</li>
        </ul>
      </DocSection>

      <DocSection id="workspace" title="2. Workspace & branding">
        <p>
          The workspace is your tenant — the isolation boundary for every other resource. Branding
          carries through to every exported report.
        </p>
        <Steps items={[
          { title: "Open Settings → Workspace", body: "Update the workspace name. This appears as the title on every report PDF / Excel cover." },
          { title: "Pick a brand color", body: "Hex color — used for emphasis blocks (Net Income, Total Liabilities + Equity, Net Change in Cash) and the reconciliation header." },
          { title: "Upload a logo", body: "Up to 2 MB, base64-encoded and stored alongside your tenant record. The logo appears at the top of every PDF cover." },
        ]} />
        <Callout kind="tip" title="Reset to defaults">
          Clear both the brand color and logo to restore Financials Ready&apos;s default navy palette across all
          exported reports.
        </Callout>
      </DocSection>

      <DocSection id="users" title="3. Users, roles & invites">
        <p>Add accountants, bookkeepers, and stakeholders by invite. Every invite is single-use and expires.</p>
        <Steps items={[
          { title: "Open Team", body: "Sidebar → Team." },
          { title: "Click 'Invite user'", body: "Pick a role (Admin, Accountant, Viewer). Owners cannot be invited — they are created at workspace signup." },
          { title: "Share the invite link", body: "Copy the generated URL and send it to the recipient. The link works once and self-expires after the configured TTL." },
          { title: "Manage existing users", body: "Toggle role or deactivate from the team table. The owner cannot be deactivated." },
        ]} />
        <Callout kind="warn" title="Team-member limits">
          Your plan caps the number of active team members. If you hit the cap, deactivate an inactive user
          or upgrade your plan before issuing a new invite.
        </Callout>
        <h4 className="font-display font-bold text-lg mt-5 mb-2">Guided-tour state per user</h4>
        <p className="text-sm">
          Every user row carries a <code>tour_state</code> object on their <code>users</code> doc:
          <code> {"{ variant, completed, dismissed, last_step_index, updated_at }"}</code>. The
          variant is derived from role (owner/admin → <code>owner</code>, accountant →
          <code> accountant</code>, viewer → <code>viewer</code>, client → <code>client</code>),
          so a role change automatically swaps which deck of steps plays next time the user hits
          “Take the tour”.
        </p>
        <ul className="list-disc pl-5 space-y-1.5 text-sm mt-2">
          <li>
            Read: <code>GET /api/tour-state</code>. Writes a fresh default
            (<code>completed=false</code>, <code>dismissed=false</code>, <code>last_step_index=0</code>)
            if the user has never seen it.
          </li>
          <li>
            Update: <code>PATCH /api/tour-state</code> with any subset of
            <code> {"{completed, dismissed, last_step_index}"}</code>. The frontend writes on every
            Next click so progress is resumable across devices.
          </li>
          <li>
            Reset for a user you want to re-onboard: hit
            <code> PATCH /api/tour-state</code> with
            <code> {"{\"completed\": false, \"dismissed\": false, \"last_step_index\": 0}"}</code>
            and ask them to clear the <code>ll_tour_seen_&lt;userId&gt;</code> key from
            <code> localStorage</code> (or sign in from a different browser) — the tour will
            auto-launch on their next login.
          </li>
          <li>
            Clients get a separate 4-step deck focused on the upload portal. The route is
            whitelisted in <code>CLIENT_ALLOWED_EXACT</code> so client-role JWTs can still save
            progress.
          </li>
        </ul>
      </DocSection>

      <DocSection id="billing" title="4. Billing & plan limits">
        <p>
          Billing is handled via Stripe Checkout. Plans differ in bank account count, seat count, and
          statement history depth.
        </p>
        <ul className="list-disc pl-5 space-y-1.5 text-sm">
          <li><strong>Starter — $29/mo</strong>: 1 bank account, 3 months history, 1 seat.</li>
          <li><strong>Pro — $79/mo</strong>: 5 bank accounts per client workspace (buy more at $3/mo), 60 months history, 5 team members (buy more at $10/mo up to 24 total), up to 25 client workspaces.</li>
          <li><strong>Business — $199/mo</strong>: unlimited bank accounts per client workspace, 60 months, 25 team members, unlimited client workspaces.</li>
        </ul>
        <Steps items={[
          { title: "Open Billing", body: "Sidebar → Billing." },
          { title: "Click 'Upgrade plan'", body: "Stripe Checkout opens in a new tab. After payment, your plan and limits update automatically." },
          { title: "Download invoices", body: "Past payment_transactions are listed in the billing screen — click any row to view." },
        ]} />
        <Callout kind="note" title="Failed payments">
          If Stripe declines a charge, your plan stays active until the end of the current period.
          We mark the workspace 'past_due' and email the owner.
        </Callout>
        <Callout kind="warn" title="Webhook signature validation">
          Every incoming Stripe webhook is signature-verified using <code>STRIPE_WEBHOOK_SECRET</code>
          from <code>backend/.env</code>. In production (with a real <code>sk_live_*</code> key), any request
          missing the <code>Stripe-Signature</code> header is rejected with a 400, and any bad signature
          is caught by the SDK&apos;s <code>construct_event</code>. In dev mode (<code>sk_test_emergent</code>)
          signatures are accepted permissively — this is intentional so local proxy replays work. Set the
          real secret in <code>.env</code> and restart the backend before pointing production traffic
          at the webhook endpoint.
        </Callout>
      </DocSection>

      <DocSection id="categories" title="5. Custom categories">
        <p>
          The default category list covers most general-purpose businesses. Add domain-specific
          categories per workspace to match your chart of accounts.
        </p>
        <Steps items={[
          { title: "Open Settings → Categories", body: "Review the default list. Defaults cannot be removed but you can add custom ones below." },
          { title: "Add a new category (Settings)", body: "Name and pick a group (Income, COGS, Expense, Other). The group decides which report bucket the category contributes to (e.g. an 'Income' custom category feeds Revenue on the P&L)." },
          { title: "Add a new category (Transactions page)", body: "Users can also create categories inline: on the Transactions page, click the + button next to the category filter, name the category, pick a group, save. It appears in every dropdown immediately and is tenant-scoped (every teammate sees it)." },
          { title: "Bulk re-categorize legacy data", body: "Use the bulk re-categorize action on the Transactions screen. Select rows via checkboxes, pick the new category from the bulk-action bar, click Apply." },
          { title: "Bulk import via CSV", body: "The Transactions page 'Import CSV' button lets users paste or upload up to 500 rows with per-row category or a bulk 'Assign category' bar in the preview. Duplicates on (bank, date, description, amount) are auto-skipped. Column mapping is saved per bank in browser localStorage." },
          { title: "Delete or bulk-delete rows", body: "The trash icon on any Transactions row (or the bulk-action bar's Delete button) removes the row(s) permanently. Reconciled rows are protected — un-reconcile first. Deleting also decrements the parent statement's counter." },
        ]} />
      </DocSection>

      <DocSection id="teller" title="6. Bank Connect setup (Teller MTLS)">
        <p>
          Teller is the integration that lets your users link real bank accounts. It uses mutual TLS:
          your backend presents a client certificate and private key issued by Teller on every API call.
        </p>
        <Steps items={[
          { title: "Create a Teller application", body: "Visit teller.io → dashboard → create an application. Note the Application ID (looks like app_...) and download the certificate.pem + private_key.pem pair." },
          { title: "Place the certs", body: "Drop the two .pem files into /app/backend/teller_certs/. The backend reads them at startup; restart after replacing them." },
          { title: "Set environment variables", body: "In backend/.env, set TELLER_APP_ID, TELLER_ENVIRONMENT (sandbox/development/production), TELLER_CERT_PATH, TELLER_KEY_PATH, and (for webhooks) TELLER_SIGNING_SECRET." },
          { title: "Verify the config endpoint", body: "Hit GET /api/bank-connect/config — it should return configured: true. If false, the message field tells you what is missing." },
          { title: "Connect a bank from the UI", body: "Open Bank Accounts and click 'Connect a bank'. In sandbox mode the password is always 'password'." },
        ]} />
        <Callout kind="warn" title="Keep your private key secret">
          The private_key.pem grants access to every linked user&apos;s financial data. Treat it like a
          production secret — restrict file permissions, never check it into git, and rotate it via the
          Teller dashboard if you suspect compromise.
        </Callout>
        <Callout kind="note" title="Sandbox vs production">
          Sandbox simulates banks without MTLS being strictly required. Development and production
          environments mandate MTLS — switch only after you have certificates issued for the target env.
        </Callout>
      </DocSection>

      <DocSection id="platform-admin" title="7. Platform admin (cross-tenant)">
        <p>
          Platform admin is a SaaS-wide superuser role for Financials Ready staff. Anyone whose
          email is listed in <code>PLATFORM_ADMIN_EMAILS</code> (server env, comma-separated)
          gets <code>is_platform_admin=true</code> on startup, plus a "Platform admin" link
          in the sidebar.
        </p>
        <ul className="list-disc pl-5 space-y-1.5 text-sm">
          <li><strong>8-KPI control room</strong> — total tenants, users, active subs, MRR, trial subs, bank accounts, statements, txns (last 30d).</li>
          <li><strong>Five tabs</strong> — Tenants, Users, Login activity, Impersonation audit, Comped accounts. All of them respect your filters and support CSV export where applicable.</li>
          <li><strong>Searchable tenant table</strong> with inline editors for plan (starter/pro/business) and subscription status. Flip a tenant to <em>comped</em> (free) by clicking "Set free" on the row — a modal demands a category (Partner / Beta tester / Support credit / Internal / Other) + a free-text reason. The grant appears in the <em>Comped accounts</em> tab with filters by category and CSV export for quarterly reviews. Revoke it with one click to flip the tenant back to active.</li>
          <li><strong>Tenant detail drawer</strong> — click any row to open 4 tabs: Overview (KV + counts), Users (with platform-admin badges), Billing (Stripe transactions), Audit log (timeline of plan changes, impersonations, flag events).</li>
          <li><strong>Users tab (bulk + single)</strong> — checkboxes on every row + a select-all. Toolbar lets you bulk-deactivate, bulk-reactivate, or bulk-reset-passwords with per-row results (partial failures surface individually, e.g. "would leave tenant without an active owner"). Single-user actions (view, edit, delete, impersonate, reset password) remain on each row.</li>
          <li><strong>Login as tenant / user</strong> — mints a 15-minute JWT marked <code>impersonated=true</code>. A modal demands a short reason (min 3 chars) before the session starts. Every impersonation is logged to the <strong>Impersonation audit</strong> tab (actor, target, tenant, reason, TTL, time) with filters for actor email, target email, and time window.</li>
          <li><strong>Login activity</strong> — per-tenant daily login counts rendered as inline SVG sparklines over the last 7 – 90 days. Dormant tenants (no login in 30+ days) get a red "Dormant" badge; use the "Dormant only" filter to spot workspaces at risk of churn.</li>
          <li><strong>Export tenant data</strong> — generates an AES-256 password-protected ZIP of every record scoped to the tenant (12 collections + manifest + README). Password returned in <code>X-Export-Password</code> response header AND persisted in the audit log so a future admin can re-derive it. Used for GDPR Art. 15 / CCPA §1798.110 data-subject access requests.</li>
        </ul>
        <Callout kind="warn" title="Everything is audited">
          Every patch, impersonation, export, lock/unlock, and comp grant/revoke is appended to the
          <code> admin_audit</code> collection with actor email + payload diff. The Impersonation audit
          tab and Comped accounts tab are specialized views over the same append-only log.
        </Callout>
      </DocSection>

      <DocSection id="compliance" title="8. Compliance (close periods, audit)">
        <p>
          Two features in Settings give owners and admins the tools auditors look for during
          month-end close.
        </p>
        <h4 className="font-display font-bold text-lg mt-4 mb-2">Closed periods (period lock)</h4>
        <ul className="list-disc pl-5 space-y-1.5 text-sm">
          <li>
            Lock a month in <strong>Settings → Closed periods</strong> (or in one tap via the
            <em> Close month</em> button on the Reconciliation page, which defaults to the previous
            calendar month).
          </li>
          <li>
            Once locked, any PATCH or DELETE on a transaction dated inside that month returns
            <code> 409 period_locked</code> and the UI shows a lock icon on the row. Only the
            <code> notes</code> field remains editable, so follow-up flagging still works even on
            closed months.
          </li>
          <li>
            Owner / admin can re-open from the same card — every unlock writes an
            <code> admin_audit</code> entry with the actor, year, month, and timestamp.
          </li>
          <li>
            Each lock stamps who closed it, when, and an optional reviewer note. Re-locking an
            already-locked month is idempotent (refreshes the note) so a scheduled job can safely
            re-run.
          </li>
        </ul>
        <h4 className="font-display font-bold text-lg mt-5 mb-2">Flag follow-up templates</h4>
        <ul className="list-disc pl-5 space-y-1.5 text-sm">
          <li>
            Manage reusable follow-up reasons in <strong>Settings → Flag follow-up templates</strong>.
            Four defaults are seeded on first visit for every tenant: <em>Awaiting receipt</em>,
            <em> Possible duplicate</em>, <em>Needs re-categorization</em>, <em>Confirm with client</em>.
          </li>
          <li>
            Reorder with the arrow controls, inline-edit the pencil icon, or delete templates that
            don&apos;t apply to your workflow. Changes are tenant-scoped.
          </li>
          <li>
            Templates surface as one-click chips in the Reconciliation verify panel&apos;s Flag modal.
            Reviewers can tap a chip (populates the note field) or type something custom.
          </li>
        </ul>
        <h4 className="font-display font-bold text-lg mt-5 mb-2">Opening-balance audit</h4>
        <ul className="list-disc pl-5 space-y-1.5 text-sm">
          <li>
            Every PATCH that changes <code>bank_accounts.opening_balance</code> appends a row to
            <code> opening_balance_history</code> with <code>previous_value</code>,
            <code> new_value</code>, <code>delta</code>, actor id/name/email, and timestamp.
          </li>
          <li>
            Read-only history endpoint: <code>GET /api/bank-accounts/{"{id}"}/opening-balance-history</code>.
            Surfaced in the UI as the clock icon beside the Reconciliation page&apos;s opening balance
            → opens a drawer with every mutation and a +/- delta badge.
          </li>
          <li>
            Suggestion endpoint: <code>GET /api/bank-accounts/{"{id}"}/opening-balance-suggestion</code>
            peeks at the earliest parsed statement&apos;s <code>opening_balance</code> field; returned to
            the UI as the &quot;Use statement&apos;s opening balance&quot; one-click chip. Reduces onboarding
            setup error — the number auditors look at first.
          </li>
        </ul>
        <h4 className="font-display font-bold text-lg mt-5 mb-2">Verify audit trail</h4>
        <p className="text-sm">
          Every <code>is_reconciled=true</code> patch stamps <code>reconciled_by</code>,
          <code> reconciled_by_name</code>, and <code>reconciled_at</code> on the transaction. The
          Verify panel surfaces this inline under each row (&quot;Verified by Priya · 03 Feb 2026&quot;)
          and in a hover tooltip on the check icon. Un-verifying clears the stamp so the next review
          is attributed cleanly.
        </p>
        <Callout kind="note" title="Opening balance edits bypass the lock">
          Opening balance is a per-account static value, not dated to a specific month, so changing
          it is allowed even if months are locked. It will however re-shift computed closing balances
          — review before changing on a reviewed ledger.
        </Callout>
      </DocSection>

      <DocSection id="demo-tenants" title="9. Demo tenants & background crons">
        <p>
          Financials Ready exposes a public zero-signup demo endpoint used by the landing-page
          <strong> &quot;Try the live demo&quot;</strong> button, plus two APScheduler cron jobs that
          keep the database healthy. This section is for platform admins who need to reason
          about these background writes.
        </p>
        <ul className="list-disc pl-5 space-y-1.5 text-sm">
          <li>
            <strong>POST /api/auth/demo</strong> — unauthenticated. Mints a tenant with
            <code> id = demo-&lt;12-hex&gt; </code>, an owner user (email
            <code> demo-&lt;12-hex&gt;@financialsready.app</code>, random 32-byte password), two
            bank accounts (checking + savings), two statements, ~400 seeded transactions
            over 12 months (mobile-tuned volume), 6 invoices (paid/partial/sent/overdue/draft),
            1 recurring invoice template, and 1 active payment plan. Returns the same
            <code>{'{ token, user, tenant }'}</code> payload as <code>/auth/login</code>.
            The tenant is placed on the <em>business</em> plan so all features are unlocked
            for the trial.
          </li>
          <li>
            <strong>Rate limit</strong>: 5 demos per source IP per hour (sliding window,
            in-memory). Exceeding the limit returns HTTP 429 with a friendly retry-after
            message. Because the counter is in-process, the effective per-host limit is
            <code> 5 × N-workers</code> — fine for a preview button, but consider a Redis-backed
            limiter before a serious marketing launch.
          </li>
          <li>
            <strong>Daily 3am UTC — dormant-user deactivation</strong>. Any non-owner,
            non-platform-admin user whose <code>last_login_at</code> is older than
            <code> DORMANT_USER_DAYS</code> (default 90) is flipped to
            <code> is_active=false</code>. An <code>admin_audit</code> row with action
            <code> user.auto_deactivate</code> is written per user. Owners are exempt so a
            tenant is never accidentally locked out.
          </li>
          <li>
            <strong>Daily 4am UTC — demo tenant cleanup</strong>. Every tenant whose
            <code> id</code> starts with <code>demo-</code> and whose <code>created_at</code>
            is older than 24 hours is purged along with ALL child rows: users, bank
            accounts, statements, transactions, memberships, custom categories,
            bank connections, and prior audit entries. A final
            <code> demo_tenant.cleanup </code>audit row is written for each purge.
          </li>
          <li>
            <strong>Daily 5am UTC — recurring rules materialisation</strong>. Every active
            row in <code>recurring_rules</code> whose <code>next_run</code> is today or
            earlier is materialised into a real <code>transactions</code> row (with
            <code> is_manual=true</code>) and the schedule is advanced by its
            <code> cadence</code>. If the current <code>next_run</code>&apos;s <code>YYYY-MM</code>
            appears in the rule&apos;s <code>skip_periods</code>, the row is advanced without
            creating a transaction (pause window). Duplicate protection: if a matching
            <code> (bank, date, description, amount) </code>row already exists (e.g. a
            statement upload created it first), the schedule still advances but no
            duplicate is inserted. Backfill loop caps at 24 iterations per rule to
            avoid runaway loops on malformed dates.
          </li>
          <li>
            <strong>Weekly Monday 9am UTC — upload reminder push</strong>. Sends a
            web-push nudge to any owner whose tenant hasn&apos;t uploaded a statement in
            the last 30 days. Respects each user&apos;s push-subscription opt-in.
          </li>
        </ul>
        <Callout kind="tip" title="Manually cleaning up mid-day">
          Need to purge demos before the 4am cron? Run
          <code> python -c &quot;from routes_demo import cleanup_expired_demo_tenants; import asyncio; asyncio.run(cleanup_expired_demo_tenants())&quot;</code>
          from <code>/app/backend</code> to fire it on demand.
        </Callout>
        <Callout kind="warn" title="Demo users can't log in">
          The demo user&apos;s password is <code>secrets.token_urlsafe(32)</code> — effectively
          unguessable. Demos are strictly session-bound: once the returned JWT expires
          or the tenant is purged, that user is inaccessible. This is intentional.
        </Callout>
      </DocSection>

      <DocSection id="data" title="9. Data lifecycle & deletes">
        <p>Cascade rules to know about before you delete anything:</p>
        <ul className="list-disc pl-5 space-y-1.5 text-sm">
          <li>Deleting a <strong>bank account</strong> deletes all related statements and transactions.</li>
          <li>Deleting a <strong>statement</strong> deletes all transactions that originated from it.</li>
          <li>Deleting a <strong>transaction</strong> is now surfaced directly on the Transactions screen — via a per-row trash icon or a bulk-action bar &quot;Delete&quot; button. The parent statement&apos;s <code>transaction_count</code> is decremented automatically so exports and dashboards stay in sync.</li>
          <li>Reconciled transactions are <strong>protected from deletion</strong> — the endpoint returns HTTP 409 and the UI disables the trash icon with a &quot;Un-reconcile first&quot; tooltip. This is a safety guard so a closed period never silently changes underneath you.</li>
          <li>Bulk delete silently <em>skips</em> reconciled rows in the selection and reports them via <code>skipped_reconciled</code> — the un-reconciled rows are still deleted.</li>
          <li>Deleting a <strong>user</strong> revokes their token and removes them from the team table; transactions they edited remain.</li>
          <li>Deleting a <strong>tenant (workspace)</strong> is a support-only action and cannot be self-served.</li>
          <li>Demo tenants (<code>id</code> prefixed <code>demo-</code>) are auto-purged after 24h by the daily 4am cron — see section 8.</li>
        </ul>
        <Callout kind="warn" title="Backups">
          Deletes are immediate. We retain MongoDB backups for 7 days — email support if you need a restore.
        </Callout>
      </DocSection>

      <DocSection id="security" title="10. Security & compliance">
        <ul className="list-disc pl-5 space-y-1.5 text-sm">
          <li>Passwords are hashed with bcrypt at cost factor 12; we never store plaintext.</li>
          <li>JWTs are signed with HS256 using the per-deployment JWT_SECRET. Tokens expire after 24h of inactivity.</li>
          <li>Stripe API keys, Teller MTLS keys, and the LLM key live in backend/.env — never in client code.</li>
          <li>Tenant isolation is enforced at the database query layer: every read and write is filtered by tenant_id.</li>
          <li>Webhook signatures (Teller, Stripe) are verified before any state mutation occurs.</li>
        </ul>
      </DocSection>

      <DocSection id="troubleshooting" title="11. Troubleshooting">
        <ul className="list-disc pl-5 space-y-2 text-sm">
          <li><strong>Statements stuck in &apos;parsing&apos;</strong> — check the backend supervisor logs for parser errors. Restart with <code>sudo supervisorctl restart backend</code> if needed.</li>
          <li><strong>AI categorization returning &apos;Uncategorized&apos;</strong> — your Emergent LLM key may be out of credit. Check Settings → Universal Key in the Emergent admin console.</li>
          <li><strong>Teller Connect button disabled</strong> — usually a missing TELLER_APP_ID or missing cert files. Use GET /api/bank-connect/config to confirm.</li>
          <li><strong>Stripe checkout opens but plan never updates</strong> — webhook signature failing. Check the webhook URL in Stripe matches your deployed backend AND that <code>STRIPE_WEBHOOK_SECRET</code> in <code>.env</code> matches Stripe&apos;s dashboard value. See section 4.</li>
          <li><strong>Statement preview says &quot;No date column detected&quot;</strong> — your CSV headers don&apos;t include a recognized date keyword. Cancel the preview, rename the column to <code>Date</code> or <code>Posted Date</code>, re-export from your bank, and try again.</li>
          <li><strong>Trash icon disabled on a transaction</strong> — the row is reconciled. Toggle the reconciled checkbox off first, then the trash icon becomes active.</li>
          <li><strong>&quot;Try the live demo&quot; returns 429</strong> — you&apos;ve exceeded the per-IP demo rate limit (5/hour). Wait, or bump <code>DEMO_RATE_LIMIT</code> in <code>routes_demo.py</code>. See section 8.</li>
          <li><strong>PDF export looks wrong</strong> — try clearing branding (Settings → reset color &amp; logo) to isolate whether the issue is brand-color or layout-related.</li>
        </ul>
        <Callout kind="tip" title="Still stuck?">
          Email support@financialsready.app with the workspace ID (Settings → Workspace) and a short description.
          A finance-literate engineer triages every report within hours.
        </Callout>
      </DocSection>
    </DocsLayout>
  );
}
